import moment from 'moment';
import { NFTCollection } from '@/graphQl/nftList.type';

interface Collection {
  data: {
    collectionBySlug: {
      id: string;
      items: NFTCollection[];
    };
  };
}

export function getItems(collection: Collection): NFTCollection[] {
  // Check if the collection structure is valid
  if (collection && collection.data && collection.data.collectionBySlug) {
    return collection.data.collectionBySlug.items || [];
  }

  // Return an empty array if the structure is not as expected
  return [];
}

export const debounce = <T extends (...args: any[]) => void>(func: T, delay: number) => {
  let timeoutId: NodeJS.Timeout | undefined;

  return function (this: any, ...args: Parameters<T>): void {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

export function getFilteredData<T extends { isSoldOut?: boolean }, K extends keyof T>(
  items: T[],
  keys: K[],
  searchValue: string,
  isSold?: boolean
): T[] {
  const searchLower = searchValue.toLowerCase();

  const filteredItems = isSold ? items.filter((item) => !item?.isSoldOut) : items;

  return filteredItems.filter((item) => keys.some((key) => String(item[key]).toLowerCase().includes(searchLower)));
}

export function isItemMatching<T, K extends keyof T>(item: T, keys: K[], searchValue: string): boolean {
  return keys.some((key) => String(item[key]).toLowerCase().includes(searchValue.toLowerCase()));
}

export function isSaleActive(startDate: string, endDate: string) {
  const now = moment();

  return now.isBetween(startDate, endDate, null, '[]');
}

export function isSaleEnded(endDate: string) {
  const now = moment();

  return now.isAfter(endDate);
}

export const truncateAddress = (address?: string) => {
  if (!address || address.length <= 10) return address;

  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

export const formatDate = (dateString?: string): string => {
  return moment(dateString).format('MMMM D, YYYY [at] h:mm A');
};

export function paginateWithData<T>(data: T[], pageNo: number, pageCount: number): T[] {
  // Ensure pageNo is a positive integer and pageCount is a positive integer
  if (pageNo < 1 || pageCount < 1) {
    return [];
  }

  const startIndex = (pageNo - 1) * pageCount;
  const endIndex = startIndex + pageCount;

  return data.slice(startIndex, endIndex);
}
